/**
 * API client.
 *
 * Two rules that the rest of the app depends on:
 *
 *  1. `credentials: "include"` on every call, so the HttpOnly session cookie is
 *     sent. The token itself is never readable by this code, which is the point
 *     — an XSS cannot steal what JavaScript cannot see.
 *  2. The CSRF token lives in memory only. Putting it in localStorage would
 *     survive a tab close and be readable by any script on the page, which
 *     removes the protection it exists to provide.
 */

import type { ApiErrorBody } from "@laundrypro/shared";

const BASE = (import.meta.env.VITE_API_BASE ?? "/api").replace(/\/$/, "");

let csrfToken: string | null = null;

export function setCsrfToken(token: string | null): void {
  csrfToken = token;
}

export function getCsrfToken(): string | null {
  return csrfToken;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly fields?: Record<string, string>,
  ) {
    super(message);
    this.name = "ApiError";
  }

  /** True when the server says this session is no longer usable. */
  get isAuthFailure(): boolean {
    return this.status === 401;
  }

  get isPermissionFailure(): boolean {
    return this.status === 403;
  }
}

interface RequestOptions {
  readonly method?: string;
  readonly body?: unknown;
  readonly formData?: FormData;
  /**
   * Extra request headers. Added for `Idempotency-Key` on money-moving calls.
   * Applied BEFORE the CSRF token below, so a caller cannot accidentally (or
   * deliberately) overwrite the CSRF header from here.
   */
  readonly headers?: Readonly<Record<string, string>>;
}

export async function apiFetch<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = options.method ?? "GET";
  const headers: Record<string, string> = { ...options.headers };

  if (csrfToken && method !== "GET" && method !== "HEAD") {
    headers["x-csrf-token"] = csrfToken;
  }

  let body: BodyInit | undefined;
  if (options.formData) {
    body = options.formData;
  } else if (options.body !== undefined) {
    headers["Content-Type"] = "application/json";
    body = JSON.stringify(options.body);
  }

  const response = await fetch(`${BASE}${path}`, {
    method,
    headers,
    body,
    credentials: "include",
  });

  const text = await response.text();
  const payload = text ? safeParse(text) : null;

  if (!response.ok) {
    const error = (payload ?? {}) as ApiErrorBody;
    throw new ApiError(
      response.status,
      error.error ?? "unknown_error",
      error.message ?? describeStatus(response.status),
      error.fields as Record<string, string> | undefined,
    );
  }

  return payload as T;
}

function safeParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

/**
 * Downloads a binary (PDF) response and saves it via the browser.
 *
 * Separate from `apiFetch` on purpose: that function always reads the body as
 * text and JSON-parses it, which would corrupt binary bytes. An error
 * response here is still JSON (the Worker's error envelope), so the failure
 * path reads exactly like `apiFetch`'s.
 */
export async function downloadFile(path: string, suggestedName: string): Promise<void> {
  const response = await fetch(`${BASE}${path}`, { method: "GET", credentials: "include" });

  if (!response.ok) {
    const text = await response.text();
    const error = (safeParse(text) ?? {}) as ApiErrorBody;
    throw new ApiError(
      response.status,
      error.error ?? "unknown_error",
      error.message ?? describeStatus(response.status),
      error.fields as Record<string, string> | undefined,
    );
  }

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  try {
    const link = document.createElement("a");
    link.href = url;
    link.download = suggestedName;
    document.body.append(link);
    link.click();
    link.remove();
  } finally {
    // Deferred, not immediate: revoking before the browser has actually
    // started the save can abort the download on some engines.
    setTimeout(() => URL.revokeObjectURL(url), 4000);
  }
}

function describeStatus(status: number): string {
  switch (status) {
    case 401:
      return "Your session has ended. Please sign in again.";
    case 403:
      return "You do not have permission to do that.";
    case 404:
      return "Not found.";
    case 409:
      return "That conflicts with the current state.";
    case 422:
      return "Some of the information provided is not valid.";
    case 429:
      return "Too many requests. Please wait and try again.";
    default:
      return "Something went wrong. Please try again.";
  }
}
